import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, type Workspace } from "../lib/api";
import { useAppContext } from "../hooks/useAppContext";
import { Icon } from "./icons";

/**
 * Top-bar workspace switcher.
 *
 * Billing, checkout and usage are all scoped to a workspace (tenant), so a
 * customer with no selected workspace hits "select a workspace" everywhere and
 * can never reach the buy flow. This control lists the customer's workspaces,
 * lets them switch, and lets them create their first one inline — then routes to
 * plan comparison so the very next step is choosing a plan. When exactly one
 * workspace exists it is auto-selected on load. A stale saved selection (a
 * workspace that no longer exists) is cleared automatically.
 */
export function WorkspaceSwitcher(): JSX.Element {
  const ctx = useAppContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Initial load: restore/validate saved selection and auto-select a lone workspace.
  useEffect(() => {
    let active = true;
    api
      .listWorkspaces()
      .then((r) => {
        if (!active) return;
        setWorkspaces(r.workspaces);
        const savedStillValid = ctx.tenantId && r.workspaces.some((w) => w.id === ctx.tenantId);
        if (ctx.tenantId && !savedStillValid) {
          ctx.clearWorkspace();
        }
        const only = r.workspaces[0];
        if (!ctx.tenantId && r.workspaces.length === 1 && only) {
          ctx.setWorkspace(only.id, only.name);
        }
      })
      .catch((e) => {
        if (active) setError(e instanceof ApiError ? e.message : "Could not load workspaces");
      });
    return () => {
      active = false;
    };
    // Run once on mount; ctx setters are stable enough for this bootstrap.
  }, []);

  // Close the menu on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const select = (w: Workspace) => {
    ctx.setWorkspace(w.id, w.name);
    setOpen(false);
  };

  const create = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const w = await api.createWorkspace(trimmed);
      setName("");
      setCreating(false);
      setOpen(false);
      ctx.setWorkspace(w.id, w.name);
      const list = await api.listWorkspaces().catch(() => null);
      if (list) setWorkspaces(list.workspaces);
      navigate("/app/billing/plans");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not create workspace");
    } finally {
      setBusy(false);
    }
  };

  const label = ctx.tenantName ?? "No workspace";

  return (
    <div ref={rootRef} style={rootStyle}>
      <button
        type="button"
        className="ax-ws"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={triggerStyle}
      >
        <span className="ax-ws__avatar" aria-hidden="true" />
        <span className="ax-ws__name">{label}</span>
        <Icon.chevronDown style={{ width: 14, height: 14, flex: "none" }} />
      </button>

      {open ? (
        <div role="menu" style={menuStyle}>
          <p style={menuHeadStyle}>Workspaces</p>

          {workspaces === null ? (
            <p style={menuMutedStyle}>Loading…</p>
          ) : workspaces.length === 0 ? (
            <p style={menuMutedStyle}>No workspaces yet. Create one to choose a plan.</p>
          ) : (
            <div style={{ display: "grid", gap: 2 }}>
              {workspaces.map((w) => {
                const active = w.id === ctx.tenantId;
                return (
                  <button
                    key={w.id}
                    type="button"
                    role="menuitem"
                    onClick={() => select(w)}
                    style={{
                      ...itemStyle,
                      background: active ? "var(--surface-selected)" : "transparent",
                    }}
                  >
                    <span>{w.name}</span>
                    {active ? <span style={{ color: "var(--brand-primary)" }}>✓</span> : null}
                  </button>
                );
              })}
            </div>
          )}

          <div style={dividerStyle} />

          {creating ? (
            <div style={{ display: "grid", gap: 8 }}>
              <input
                aria-label="New workspace name"
                placeholder="Workspace name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void create();
                }}
                autoFocus
                style={inputStyle}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => void create()}
                  disabled={busy || name.trim().length < 2}
                  style={primaryBtnStyle}
                >
                  {busy ? "Creating…" : "Create & choose plan"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCreating(false);
                    setName("");
                  }}
                  disabled={busy}
                  style={ghostBtnStyle}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button type="button" role="menuitem" onClick={() => setCreating(true)} style={addStyle}>
              + New workspace
            </button>
          )}

          {error ? (
            <p role="status" style={{ ...menuMutedStyle, color: "var(--brand-secondary)" }}>
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const rootStyle: CSSProperties = { position: "relative" };

const triggerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "none",
  border: "none",
  color: "inherit",
  font: "inherit",
  cursor: "pointer",
  padding: 0,
};

const menuStyle: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 8px)",
  left: 0,
  minWidth: 268,
  background: "var(--surface-raised)",
  border: "1px solid var(--border-default)",
  borderRadius: 12,
  padding: 10,
  zIndex: 60,
  boxShadow: "0 12px 34px rgba(0, 0, 0, 0.45)",
};

const menuHeadStyle: CSSProperties = {
  margin: "2px 6px 8px",
  fontSize: "var(--text-xs)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
};

const menuMutedStyle: CSSProperties = {
  margin: "6px",
  fontSize: "var(--text-sm)",
  color: "var(--text-tertiary)",
};

const itemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "8px 10px",
  border: "none",
  borderRadius: 8,
  color: "var(--text-primary)",
  font: "inherit",
  fontSize: "var(--text-sm)",
  textAlign: "left",
  cursor: "pointer",
};

const dividerStyle: CSSProperties = {
  height: 1,
  background: "var(--border-subtle)",
  margin: "8px 0",
};

const inputStyle: CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border-strong)",
  background: "var(--surface-default)",
  color: "var(--text-primary)",
  font: "inherit",
  fontSize: "var(--text-sm)",
  width: "100%",
  boxSizing: "border-box",
};

const primaryBtnStyle: CSSProperties = {
  flex: 1,
  padding: "8px 10px",
  borderRadius: 8,
  border: "none",
  background: "var(--brand-primary)",
  color: "var(--text-inverse)",
  font: "inherit",
  fontSize: "var(--text-sm)",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border-default)",
  background: "transparent",
  color: "var(--text-secondary)",
  font: "inherit",
  fontSize: "var(--text-sm)",
  cursor: "pointer",
};

const addStyle: CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  border: "1px dashed var(--border-strong)",
  borderRadius: 8,
  background: "transparent",
  color: "var(--text-secondary)",
  font: "inherit",
  fontSize: "var(--text-sm)",
  textAlign: "left",
  cursor: "pointer",
};
